import { createSelector } from "@reduxjs/toolkit";
import { runtimeConfig } from "@src/runtime-config";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { currentUserSelector, loggedInUserSelector } from "@store/user/user-selector";
import { DEFAULT_WAYPOINT_COLOR } from "@constants/waypoint-colors";
import { getPreferredAreaUnit } from "@utils/unit-utils";
/**
 * Get the user store settings for the selected user.
 */
export const userSettingsSelector = createSelector([state => state.settings.user, loggedInUserSelector, currentUserSelector], (userSettings, loggedInUser, currentUser) => {
  const userId = loggedInUser?.id ?? currentUser?.id;
  if (!userId) {
    return {};
  }
  return userSettings.data[userId] ?? {};
});

/**
 * Selects the area unit that matches the user's preferred unit system.
 * Falls back to the region default when the user has no preferred unit set.
 */
export const preferredAreaUnitSelector = createSelector([userSettingsSelector], userSettings => getPreferredAreaUnit({
  unitMeasurement: userSettings.unit_measurement?.value,
  appRegion: runtimeConfig.appRegion
}));

/**
 * Selects the user's custom waypoint color from the store.
 * Falls back to the default blue if the setting hasn't been saved yet.
 */
export const waypointColorSelector = createSelector([state => state.settings.company, selectedSdbCompanyIdSelector], (companySettings, selectedCompanyId) => {
  const companySetting = selectedCompanyId ? companySettings[selectedCompanyId]?.find(item => item.identifier === "waypoints_custom_color") : undefined;
  const value = companySetting?.value;
  return typeof value === "string" && value.length > 0 ? value : DEFAULT_WAYPOINT_COLOR;
});

/**
 * Get the fetching status of a specific user setting.
 * Tracked per identifier so fetching/updating one setting does not affect the
 * loading state of unrelated settings.
 * @param identifier The user setting identifier to read the status for.
 */
export function fetchingUserSettingSelector(identifier) {
  return state => state.settings.user.requestStateByIdentifier[identifier] ?? "uninitialized";
}

/**
 * Get the current company settings.
 * @param companyId The ID of the company for which to get settings.
 */
export function companySettingsSelector(companyId) {
  return createSelector(state => state.settings.company, companySettings => {
    return companySettings[companyId] ?? [];
  });
}

/**
 * Get the company settings map.
 * This is a map of identifiers to CompanySettingsResponse.
 * @param companyId The ID of the company for which to get settings.
 */
export function companySettingsMapSelector(companyId) {
  return createSelector(companySettingsSelector(companyId), companySettings => {
    return companySettings.reduce((map, setting) => {
      map[setting.identifier] = setting;
      return map;
    }, {});
  });
}

/**
 * Get a company setting by its identifier.
 * @param companyId The ID of the company for which to get settings.
 * @param identifier The identifier of the company setting.
 * @returns The company setting or undefined if not found.
 */
export function companySettingByIdentifierSelector(companyId, identifier) {
  return createSelector(companySettingsMapSelector(companyId), companySettingsMap => {
    // Make shallow copy so can be modified without touching the store.
    // Helpful e.g. when changing then sending it to the backend (which would then update the store).
    const setting = companySettingsMap[identifier];
    return setting ? {
      ...setting
    } : undefined;
  });
}